/** The Booking-system sync page (`/import/sync`; backend self-signup decision D17): a client imports
 *  products of its booking system as tours (a product sync), reads its auxiliary information —
 *  guides, pickup points, ticket types, custom fields — (an aux sync) and imports its bookings (the
 *  booking sync form, booking sync plan WP-D), during and after onboarding.
 *  Contract: apiv2/dashboard_sync.openapi.yaml.
 *
 *  Starting needs `import.sync_products` / `import.sync_aux` / `ops.sync_bookings`; without it the card is
 *  read-only (the latest run is still shown and followed). Runs are background tasks: the page follows them
 *  on the bg-task API, and Approve / Cancel follow the run's `can_approve` / `can_cancel`. */
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, Link, MenuItem,
    Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import * as React from 'react';

import { backgroundTaskActionsRef } from '../bg_tasks/BackgroundTaskProvider';
import TaskProgressBar from '../bg_tasks/TaskProgressBar';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { errorCode, SyncApi, SyncApiError, useSyncApi } from './api';
import BookingSyncForm from './bookings/BookingSyncForm';
import { BOOKING_SYNC_PERMISSION, errorText, TASK_STATES } from './labels';
import type { BackgroundTaskRow, SyncCatalogue, SyncIntegration, SyncOptions, SyncProduct } from './types';
import { isActive } from './types';
import { useTask } from './useTask';

export { SYNC_PATH } from './routes';
/** The most products one product sync imports (the contract's `maxItems`). */
export const MAX_PRODUCTS = 500;
/** Rows the product list renders at once; the filter narrows the rest. */
export const MAX_ROWS_SHOWN = 200;

const Card = ({ title, children, testId }: { title: string; children: React.ReactNode; testId: string }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid={testId}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500, mb: 1 }}>{title}</Typography>
        {children}
    </Paper>
);

const STATE_COLORS: Record<number, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
    0: 'default', 1: 'primary', 2: 'success', 3: 'error', 4: 'warning', 5: 'info',
};

/** One run, followed until it settles: its state, progress, error, and what this user may do with it. */
export function RunStatus({ api, taskId, approveHint, onSettled }: {
    api: SyncApi;
    taskId: string;
    approveHint?: string;
    onSettled?: (task: BackgroundTaskRow) => void;
}) {
    const { task, error, refresh } = useTask(api, taskId, onSettled);
    const [busy, setBusy] = React.useState(false);
    const [actionError, setActionError] = React.useState<unknown>(null);

    const act = async (call: () => Promise<unknown>) => {
        setBusy(true);
        setActionError(null);
        try {
            await call();
        } catch (e) {
            setActionError(e);
        } finally {
            setBusy(false);
            refresh();
        }
    };

    if (!task) {
        return error ? <Alert severity="warning">{errorText(error)}</Alert> : <CircularProgress size={20} aria-label="Loading the run" />;
    }
    return (
        <Box data-testid={`run-${task.id}`} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{task.task_label}</Typography>
                <Chip size="small" label={TASK_STATES[task.state] || task.state_display} color={STATE_COLORS[task.state] || 'default'} data-testid="run-state" />
            </Stack>
            {task.state === 1 && task.progress && <Box sx={{ mt: 1, maxWidth: 480 }}><TaskProgressBar progress={task.progress} /></Box>}
            {task.state === 3 && task.error_message && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>{task.error_message}</Typography>
            )}
            {task.state === 5 && approveHint && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '68ch' }}>{approveHint}</Typography>
            )}
            {actionError != null && <Alert severity="warning" sx={{ mt: 1 }} data-testid="run-action-error">{errorText(actionError)}</Alert>}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {task.can_approve && task.state === 5 && (
                    <Button size="small" variant="contained" disabled={busy} onClick={() => act(() => api.approve(task.id))}>Approve</Button>
                )}
                {task.can_cancel && isActive(task.state) && (
                    <Button size="small" color="warning" variant="outlined" disabled={busy} onClick={() => act(() => api.cancel(task.id))}>Cancel</Button>
                )}
                <Button size="small" variant="text" onClick={() => backgroundTaskActionsRef.current?.openDialogForTask(task.id)}>Details and log</Button>
            </Stack>
        </Box>
    );
}

const productLine = (p: SyncProduct) => p.facets.map((f) => `${f.label}: ${f.value}`).join(' · ');

/** The product list with a filter and checkboxes; `imported` tours under each product. */
function ProductPicker({ catalogue, selected, onChange, disabled }: {
    catalogue: SyncCatalogue;
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    disabled: boolean;
}) {
    const [filter, setFilter] = React.useState('');
    const needle = filter.trim().toLowerCase();
    const matching = needle
        ? catalogue.products.filter((p) => p.name.toLowerCase().includes(needle) || p.id.toLowerCase().includes(needle))
        : catalogue.products;
    const shown = matching.slice(0, MAX_ROWS_SHOWN);
    const allShown = shown.length > 0 && shown.every((p) => selected.has(p.id));
    const toggle = (id: string) => {
        const next = new Set(selected);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        onChange(next);
    };
    const toggleShown = () => {
        const next = new Set(selected);
        shown.forEach((p) => (allShown ? next.delete(p.id) : next.add(p.id)));
        onChange(next);
    };
    return (
        <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                <TextField size="small" label="Filter products" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 260 }} />
                <FormControlLabel
                    control={<Checkbox checked={allShown} onChange={toggleShown} disabled={disabled || shown.length === 0} />}
                    label={`Select all shown (${shown.length})`}
                />
            </Stack>
            {matching.length > shown.length && (
                <Typography variant="caption" color="text.secondary" display="block" data-testid="rows-capped">
                    {`Showing ${shown.length} of ${matching.length}; filter to find the others.`}
                </Typography>
            )}
            <Box component="ul" aria-label="Products" sx={{ listStyle: 'none', p: 0, m: 0, maxHeight: 420, overflowY: 'auto' }}>
                {shown.map((p) => (
                    <Box component="li" key={p.id} data-testid={`product-${p.id}`} sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                        <Checkbox
                            size="small"
                            checked={selected.has(p.id)}
                            onChange={() => toggle(p.id)}
                            disabled={disabled}
                            inputProps={{ 'aria-label': p.name || p.id }}
                        />
                        <Box sx={{ pt: 0.75, minWidth: 0 }}>
                            <Typography variant="body2">{p.name || p.id}</Typography>
                            {p.facets.length > 0 && <Typography variant="caption" color="text.secondary" display="block">{productLine(p)}</Typography>}
                            {p.tours.length > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block" data-testid="imported-as">
                                    {`Imported as ${p.tours.map((t) => t.name).join(', ')} — importing again updates it`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/** A product sync: load the product list (a background read), pick, import. */
function ProductsCard({ api, integration, account, canStart, latestTaskId, onStarted }: {
    api: SyncApi;
    integration: SyncIntegration;
    account: string | null;
    canStart: boolean;
    latestTaskId: string | null;
    onStarted: () => void;
}) {
    const [catalogue, setCatalogue] = React.useState<SyncCatalogue | null>(null);
    const [loadTaskId, setLoadTaskId] = React.useState<string | null>(null);
    const [checking, setChecking] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [selected, setSelected] = React.useState<Set<string>>(new Set());
    const [starting, setStarting] = React.useState(false);
    const [runTaskId, setRunTaskId] = React.useState<string | null>(latestTaskId);
    const alive = React.useRef(true);
    React.useEffect(() => () => {
        alive.current = false;
    }, []);

    React.useEffect(() => setRunTaskId(latestTaskId), [latestTaskId]);

    const readList = React.useCallback(async (signal?: AbortSignal) => {
        try {
            const list = await api.catalogue(integration.integration, account, signal);
            if (alive.current) {
                setCatalogue(list);
                setError(null);
            }
        } catch (e: any) {
            if ((e && e.name === 'AbortError') || !alive.current) {
                return;
            }
            if (errorCode(e) !== 'catalogue_not_loaded') {
                setError(e);
            }
            setCatalogue(null);
        }
    }, [api, integration.integration, account]);

    // A list loaded earlier (kept an hour) shows at once.
    React.useEffect(() => {
        setCatalogue(null);
        setSelected(new Set());
        setLoadTaskId(null);
        setError(null);
        if (!canStart) {
            return undefined;
        }
        const controller = new AbortController();
        readList(controller.signal);
        return () => controller.abort();
    }, [canStart, readList]);

    const load = async (refresh: boolean) => {
        setChecking(true);
        setError(null);
        try {
            const answer = await api.loadCatalogue(integration.integration, account, refresh);
            if (answer.status === 'ready') {
                setCatalogue(answer.catalogue);
            } else {
                setLoadTaskId(answer.task_id);
            }
        } catch (e) {
            setError(e);
        } finally {
            setChecking(false);
        }
    };

    const onListRead = React.useCallback((task: BackgroundTaskRow) => {
        setLoadTaskId(null);
        if (task.state === 2) {
            readList();
        } else {
            setError(new SyncApiError(0, { detail: task.error_message || "The product list couldn't be read from your booking system. Try again later." }));
        }
    }, [readList]);

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            const run = await api.syncProducts(integration.integration, account, Array.from(selected));
            setRunTaskId(run.task_id);
            setSelected(new Set());
            onStarted();
        } catch (e: any) {
            setError(e);
            if (errorCode(e) === 'sync_running' && e.body?.task_id) {
                setRunTaskId(e.body.task_id);
            }
        } finally {
            setStarting(false);
        }
    };

    const tooMany = selected.size > MAX_PRODUCTS;
    return (
        <Card title="Products" testId="products-card">
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                {`Import products from ${integration.label} as tours. A product you imported before is updated.`}
            </Typography>
            {!canStart && (
                <Alert severity="info" sx={{ mt: 1.5 }} data-testid="products-read-only">
                    Starting a product sync needs the “Import: sync products” permission. Ask an account admin.
                </Alert>
            )}
            {runTaskId && (
                <RunStatus api={api} taskId={runTaskId} />
            )}
            {canStart && (
                <Box sx={{ mt: 1.5 }}>
                    {error != null && <Alert severity="warning" sx={{ mb: 1 }} data-testid="products-error">{errorText(error)}</Alert>}
                    {loadTaskId && (
                        <Box data-testid="list-loading">
                            <Typography variant="body2">Reading the product list from your booking system…</Typography>
                            <RunStatus api={api} taskId={loadTaskId} onSettled={onListRead} />
                        </Box>
                    )}
                    {!catalogue && !loadTaskId && (
                        <Button variant="outlined" onClick={() => load(false)} disabled={checking}>Load the product list</Button>
                    )}
                    {catalogue && (
                        <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="body2" color="text.secondary">
                                    {`${catalogue.count} product${catalogue.count === 1 ? '' : 's'}${catalogue.truncated ? ' (the list stopped at its limit)' : ''}`}
                                </Typography>
                                <Link component="button" variant="body2" onClick={() => load(true)} disabled={checking || !!loadTaskId}>Read the list again</Link>
                            </Stack>
                            {catalogue.blocked && <Alert severity="info" data-testid="list-blocked">{catalogue.blocked}</Alert>}
                            {catalogue.products.length > 0 && (
                                <ProductPicker catalogue={catalogue} selected={selected} onChange={setSelected} disabled={starting} />
                            )}
                            <Box>
                                {tooMany && (
                                    <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>{`Choose at most ${MAX_PRODUCTS} products per sync.`}</Typography>
                                )}
                                <Button
                                    variant="contained"
                                    onClick={start}
                                    disabled={starting || selected.size === 0 || tooMany || !!catalogue.blocked}
                                    data-testid="start-products"
                                >
                                    {selected.size > 0 ? `Import ${selected.size} product${selected.size === 1 ? '' : 's'}` : 'Import products'}
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </Box>
            )}
        </Card>
    );
}

/** An aux sync: one button; the run pauses for an approval. */
function AuxCard({ api, integration, account, canStart, latestTaskId, onStarted }: {
    api: SyncApi;
    integration: SyncIntegration;
    account: string | null;
    canStart: boolean;
    latestTaskId: string | null;
    onStarted: () => void;
}) {
    const [runTaskId, setRunTaskId] = React.useState<string | null>(latestTaskId);
    const [starting, setStarting] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    React.useEffect(() => setRunTaskId(latestTaskId), [latestTaskId]);

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            const run = await api.syncAux(integration.integration, account);
            setRunTaskId(run.task_id);
            onStarted();
        } catch (e: any) {
            setError(e);
            if (errorCode(e) === 'sync_running' && e.body?.task_id) {
                setRunTaskId(e.body.task_id);
            }
        } finally {
            setStarting(false);
        }
    };

    return (
        <Card title="Guides, pickup points and other details" testId="aux-card">
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                {`Read guides, drivers, pickup points, ticket types and custom fields from a sample of last week's bookings in ${integration.label}. The sync stops for your approval before anything is changed.`}
            </Typography>
            {!canStart && (
                <Alert severity="info" sx={{ mt: 1.5 }} data-testid="aux-read-only">
                    Starting this sync needs the “Import: sync auxiliary information” permission. Ask an account admin.
                </Alert>
            )}
            {runTaskId && (
                <RunStatus
                    api={api}
                    taskId={runTaskId}
                    approveHint="Approving applies what the sync suggests. Our team can go through it with you first if you prefer."
                />
            )}
            {canStart && (
                <Box sx={{ mt: 1.5 }}>
                    {error != null && <Alert severity="warning" sx={{ mb: 1 }} data-testid="aux-error">{errorText(error)}</Alert>}
                    <Button variant="contained" onClick={start} disabled={starting} data-testid="start-aux">
                        {`Read from ${integration.label}`}
                    </Button>
                </Box>
            )}
        </Card>
    );
}

/** A booking sync: the booking sync form (Settings › Sync Bookings') on the page's booking system and account. */
function BookingsCard({ api, integration, account, canStart, latestTaskId, onStarted }: {
    api: SyncApi;
    integration: SyncIntegration;
    account: string | null;
    canStart: boolean;
    latestTaskId: string | null;
    onStarted: () => void;
}) {
    const [runTaskId, setRunTaskId] = React.useState<string | null>(latestTaskId);
    React.useEffect(() => setRunTaskId(latestTaskId), [latestTaskId]);
    const scope = React.useMemo(() => ({ integration: integration.integration, account }), [integration.integration, account]);
    const onLaunched = React.useCallback((taskId: string) => {
        setRunTaskId(taskId);
        onStarted();
    }, [onStarted]);

    return (
        <Card title="Bookings" testId="bookings-card">
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                {`Import bookings from ${integration.label}: by dates and tours, or by booking IDs. Guests get no notifications or surveys unless you switch them on under Advanced options.`}
            </Typography>
            {!canStart && (
                <Alert severity="info" sx={{ mt: 1.5 }} data-testid="bookings-read-only">{BOOKING_SYNC_PERMISSION}</Alert>
            )}
            {runTaskId && (
                <RunStatus
                    api={api}
                    taskId={runTaskId}
                    approveHint="Approving imports the bookings the sync fetched. Open Details and log to look at them first."
                />
            )}
            {canStart && (
                <Box sx={{ mt: 2 }}>
                    <BookingSyncForm scope={scope} onLaunched={onLaunched} />
                </Box>
            )}
        </Card>
    );
}

/** The account a booking system syncs unless another is picked: its default, else its first. */
const defaultAccount = (integration: SyncIntegration | null): string | null => {
    const account = integration ? integration.accounts.find((a) => a.is_default) || integration.accounts[0] : null;
    return account ? account.account : null;
};

const SyncPage = () => {
    const api = useSyncApi();
    const [options, setOptions] = React.useState<SyncOptions | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    // The booking system and account picked; none picked = the first system and its default account.
    const [choice, setChoice] = React.useState<{ integration: string; account: string | null } | null>(null);
    const [version, setVersion] = React.useState(0);

    React.useEffect(() => {
        const controller = new AbortController();
        api.options(controller.signal).then((o) => {
            setOptions(o);
            setError(null);
        }).catch((e) => {
            if (!(e && e.name === 'AbortError')) {
                setError(e);
            }
        });
        return () => controller.abort();
    }, [api, version]);

    const integrations = options?.integrations || [];
    const integration = integrations.find((i) => i.integration === choice?.integration) || integrations[0] || null;
    const account = integration && choice?.integration === integration.integration ? choice.account : defaultAccount(integration);
    const reload = React.useCallback(() => setVersion((n) => n + 1), []);

    const refused = error instanceof SyncApiError && error.status >= 400 && error.status < 500;
    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 }, maxWidth: 1080 }} data-testid="sync-page">
            <PageMeta title="Booking-system sync" />
            <PageHeading>Booking-system sync</PageHeading>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: '72ch' }}>
                Bring products, bookings and other details over from your booking system. A sync runs in the background: you can leave this page and follow it under Background tasks.
            </Typography>

            {!options && !error && <CircularProgress size={28} />}
            {error != null && (
                <Alert severity={refused ? 'info' : 'warning'} data-testid="sync-refused">
                    {refused && error instanceof SyncApiError && error.status === 403
                        ? "You don't have access to booking-system sync. Ask an account admin for the “Import: sync products”, “Import: sync auxiliary information” or “Ops: sync bookings” permission."
                        : errorText(error)}
                </Alert>
            )}

            {options && integrations.length === 0 && (
                <Alert severity="info" data-testid="no-restech">
                    No booking system is connected to your account yet. Once one is, you can sync from it here.
                </Alert>
            )}

            {options && integration && (
                <Stack spacing={2}>
                    {(integrations.length > 1 || integration.accounts.length > 1) && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            {integrations.length > 1 && (
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel id="sync-integration">Booking system</InputLabel>
                                    <Select labelId="sync-integration" label="Booking system" value={integration.integration} onChange={(e) => {
                                        const code = String(e.target.value);
                                        setChoice({ integration: code, account: defaultAccount(integrations.find((i) => i.integration === code) || null) });
                                    }}>
                                        {integrations.map((i) => <MenuItem key={i.integration} value={i.integration}>{i.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                            {integration.accounts.length > 1 && (
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel id="sync-account">Account</InputLabel>
                                    <Select
                                        labelId="sync-account"
                                        label="Account"
                                        value={account ?? ''}
                                        onChange={(e) => setChoice({ integration: integration.integration, account: String(e.target.value) || null })}
                                    >
                                        {integration.accounts.map((a) => <MenuItem key={a.account ?? ''} value={a.account ?? ''}>{a.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>
                    )}
                    {integration.products && (
                        <ProductsCard
                            key={`p-${integration.integration}-${account ?? ''}`}
                            api={api}
                            integration={integration}
                            account={account}
                            canStart={options.permissions.sync_products}
                            latestTaskId={options.latest.products?.task_id ?? null}
                            onStarted={reload}
                        />
                    )}
                    {integration.aux && (
                        <AuxCard
                            key={`a-${integration.integration}-${account ?? ''}`}
                            api={api}
                            integration={integration}
                            account={account}
                            canStart={options.permissions.sync_aux}
                            latestTaskId={options.latest.aux?.task_id ?? null}
                            onStarted={reload}
                        />
                    )}
                    {/* A field the backend does not send reads as false (contract: dashboard_sync, booking sync). */}
                    {integration.bookings === true && (
                        <BookingsCard
                            key={`b-${integration.integration}-${account ?? ''}`}
                            api={api}
                            integration={integration}
                            account={account}
                            canStart={options.permissions.sync_bookings === true}
                            latestTaskId={options.latest.bookings?.task_id ?? null}
                            onStarted={reload}
                        />
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default SyncPage;
